/**
 * AetherFifaCatalogue — IFixtureCatalogue adapter over Aether GraphQL.
 *
 * Reads the existing `allUpcomingFixtures` query Dashboard already
 * uses; transforms the response into IFixtureCatalogue's
 * transport-agnostic FixtureSummary / CompetitionSummary shapes.
 *
 * Single Responsibility: knows only the Aether GraphQL transport +
 * how to shape its response. Doesn't decide where to fetch from
 * (caller injects the client), doesn't render anything, doesn't own
 * any React state.
 *
 * Dependency Inversion: takes the client as an injected param. The
 * production code passes the real Apollo client; tests pass a fake
 * with the same `query({ query, variables })` shape.
 */

import { gql } from "@apollo/client";

const ALL_UPCOMING_FIXTURES = gql`
  query AllUpcomingFixtures($competitionIds: [String!], $databaseName: String) {
    allUpcomingFixtures(
      competitionIds: $competitionIds,
      databaseName: [$databaseName]
    )
  }
`;

// Dev / placeholder rows the server has historically returned. Filter
// here so consumers never see them in the picker.
const PLACEHOLDER_LABELS = new Set(["Home Team v Away Team"]);

function _fixtureLabel(fx) {
  if (fx?.fixtureName) return fx.fixtureName;
  if (fx?.homeTeamName && fx?.awayTeamName) {
    return `${fx.homeTeamName} vs ${fx.awayTeamName}`;
  }
  return "Unnamed Fixture";
}

function _isPlaceholder(label) {
  return PLACEHOLDER_LABELS.has(label);
}

function _toFixtureSummary(fx) {
  return {
    id: fx?.aetFixtureId,
    label: _fixtureLabel(fx),
    competition: fx?.competitionName,
    startDateTime: fx?.startDateTime,
    raw: fx,
  };
}

export function createAetherFifaCatalogue({ client, competitionId, databaseName }) {
  if (!client) throw new Error("AetherFifaCatalogue: `client` is required");
  if (!databaseName) throw new Error("AetherFifaCatalogue: `databaseName` is required");
  // `competitionId` is intentionally NOT required. getCompetitions()
  // doesn't use it, and the host needs a catalogue to discover
  // competitions BEFORE it knows which one to filter on. listFixtures()
  // guards for it instead.

  return {
    /** @returns {Promise<import("../IFixtureCatalogue").FixtureSummary[]>} */
    async listFixtures() {
      if (!competitionId) return [];
      const response = await client.query({
        query: ALL_UPCOMING_FIXTURES,
        variables: {
          competitionIds: [competitionId],
          databaseName,
        },
      });

      const competitions = response?.data?.allUpcomingFixtures ?? [];
      const fixtures = [];
      for (const competition of competitions) {
        const inner = competition?.fixtures ?? [];
        for (const fx of inner) {
          const label = _fixtureLabel(fx);
          if (_isPlaceholder(label)) continue;
          fixtures.push(_toFixtureSummary(fx));
        }
      }
      return fixtures;
    },

    /**
     * Discover every competition present on the tenant.
     *
     * The Aether competition documents only carry `_id` / `name`
     * (mongo ObjectIds) — NOT the UUID the server filters fixtures by.
     * The filterable competition id lives on each fixture as
     * `aetCompetitionId` (+ `competitionName`). So we query WITHOUT a
     * competitionIds filter (otherwise we'd only ever see the one this
     * catalogue was constructed for) and collect the distinct pairs
     * off the fixtures.
     *
     * @returns {Promise<import("../IFixtureCatalogue").CompetitionSummary[]>}
     */
    async getCompetitions() {
      const response = await client.query({
        query: ALL_UPCOMING_FIXTURES,
        variables: { databaseName },
      });
      const competitionDocs = response?.data?.allUpcomingFixtures ?? [];
      const seen = new Map();
      for (const doc of competitionDocs) {
        for (const fx of doc?.fixtures ?? []) {
          const id = fx?.aetCompetitionId;
          if (!id || seen.has(id)) continue;
          seen.set(id, { id, name: fx?.competitionName || id });
        }
      }
      return [...seen.values()];
    },
  };
}
